// src/pages/TalentConnect.tsx: manager-facing candidate discovery + job management
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import {
  C, Candidate, TalentJob, TalentJobFlag, TalentConnection, AIMatch,
  TARGET_ROLES, ASSET_CLASSES, SENIORITIES, RELOCATION_COUNTRIES,
  matchCandidatesWithAI,
} from '../lib/talentConnect';
import { card, inputStyle, labelStyle, btnPrimary, btnGhost, Banner, LoadingScreen, fmtDate, ChipSelect, Pills, SeniorityBadge } from '../lib/talentUI';

type TabKey = 'candidates' | 'jobs';

export default function TalentConnect() {
  const { profile } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: TabKey = searchParams.get('tab') === 'jobs' ? 'jobs' : 'candidates';

  const goToTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'candidates') params.delete('tab');
    else params.set('tab', next);
    // Job pre-selection only applies to the candidates tab.
    if (next !== 'candidates') params.delete('job');
    setSearchParams(params);
  };

  const header = (
    <div style={{ marginBottom: '4px' }}>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>TalentConnect</h1>
      <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Find talent for your firm and manage your open roles</p>
    </div>
  );

  const tabBtn = (key: TabKey, label: string) => {
    const active = tab === key;
    return (
      <button
        onClick={() => goToTab(key)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '13.5px', fontWeight: active ? 700 : 500, color: active ? C.navy : C.muted,
          padding: '10px 2px', marginRight: '24px', borderBottom: `2px solid ${active ? C.navy : 'transparent'}`,
          letterSpacing: '0.1px',
        }}
      >
        {label}
      </button>
    );
  };

  const tabBar = (
    <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${C.border}`, margin: '18px 0 22px' }}>
      {tabBtn('candidates', 'Find Candidates')}
      {tabBtn('jobs', 'My Jobs')}
    </div>
  );

  const content = (children: React.ReactNode) => (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {header}
      {tabBar}
      {children}
    </div>
  );

  // Managers only.
  if (profile && profile.role !== 'manager') {
    return content(<Banner type="info" text="TalentConnect is available to hiring managers." />);
  }

  return content(tab === 'jobs'
    ? <MyJobsTab />
    : <FindCandidatesTab goToTab={goToTab} />);
}

/* ─────────────────────────── Find Candidates ─────────────────────────── */

function FindCandidatesTab({ goToTab }: { goToTab: (t: TabKey) => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { firm } = useProfile();
  const [searchParams] = useSearchParams();

  const [jobs, setJobs]         = useState<TalentJob[]>([]);
  const [candidates, setCands]  = useState<Candidate[]>([]);
  const [flags, setFlags]       = useState<TalentJobFlag[]>([]);
  const [connections, setConns] = useState<TalentConnection[]>([]);
  const [loading, setLoading]   = useState(true);
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [connecting, setConnecting]       = useState<string | null>(null);

  const [showFilters, setShowFilters] = useState(false);
  const [fSeniority, setFSeniority] = useState<string[]>([]);
  const [fRoles, setFRoles]         = useState<string[]>([]);
  const [fAssets, setFAssets]       = useState<string[]>([]);
  const [fReloc, setFReloc]         = useState<string[]>([]);

  const [aiMatches, setAiMatches] = useState<Record<string, AIMatch> | null>(null);
  const [aiBusy, setAiBusy]       = useState(false);
  const [aiErr, setAiErr]         = useState('');

  useEffect(() => { if (firm !== undefined) load(); /* eslint-disable-next-line */ }, [firm]);

  async function load() {
    setLoading(true);
    if (!firm) { setLoading(false); return; }
    const [{ data: jobData }, { data: candData }, { data: connData }] = await Promise.all([
      supabase.from('talent_jobs').select('*').eq('firm_id', firm.id).eq('status', 'active').order('created_at', { ascending: false }),
      supabase.from('candidates').select('*').eq('profile_complete', true),
      supabase.from('talent_connections').select('*').eq('manager_firm_id', firm.id),
    ]);
    const jobList = (jobData as TalentJob[]) || [];
    setJobs(jobList);
    setCands((candData as Candidate[]) || []);
    setConns((connData as TalentConnection[]) || []);

    // Pre-select from ?job= if valid, else first active job.
    const wanted = searchParams.get('job');
    const initial = jobList.find(j => j.id === wanted)?.id || jobList[0]?.id || '';
    setSelectedJobId(initial);

    if (jobList.length) {
      const { data: flagData } = await supabase.from('talent_job_flags')
        .select('*').in('job_id', jobList.map(j => j.id));
      setFlags((flagData as TalentJobFlag[]) || []);
    }
    setLoading(false);
  }

  const selectedJob = jobs.find(j => j.id === selectedJobId) || null;

  // Candidate ids flagged to the selected job.
  const flaggedIds = useMemo(
    () => new Set(flags.filter(f => f.job_id === selectedJobId).map(f => f.candidate_id)),
    [flags, selectedJobId],
  );

  // Reset AI ranking when the job changes.
  useEffect(() => { setAiMatches(null); setAiErr(''); }, [selectedJobId]);

  // Visible candidates for this job = visible_on_platform OR flagged to this job.
  const visibleForJob = useMemo(
    () => candidates.filter(c => c.visible_on_platform || flaggedIds.has(c.id)),
    [candidates, flaggedIds],
  );

  const filtered = useMemo(() => {
    return visibleForJob.filter(c => {
      if (fSeniority.length && !(c.seniority && fSeniority.includes(c.seniority))) return false;
      if (fRoles.length && !(c.target_roles || []).some(r => fRoles.includes(r))) return false;
      if (fAssets.length && !(c.asset_classes || []).some(a => fAssets.includes(a))) return false;
      if (fReloc.length && !(c.relocation_countries || []).some(r => fReloc.includes(r))) return false;
      return true;
    });
  }, [visibleForJob, fSeniority, fRoles, fAssets, fReloc]);

  // Ordering: AI score desc when active, else flagged first.
  const ordered = useMemo(() => {
    const list = [...filtered];
    if (aiMatches) {
      list.sort((a, b) => (aiMatches[b.id]?.match_score || 0) - (aiMatches[a.id]?.match_score || 0));
    } else {
      list.sort((a, b) => Number(flaggedIds.has(b.id)) - Number(flaggedIds.has(a.id)));
    }
    return list;
  }, [filtered, aiMatches, flaggedIds]);

  const connByCandidate = (candidateId: string) =>
    connections.find(c => c.candidate_id === candidateId && c.job_id === selectedJobId);

  const runAIMatch = async () => {
    if (!selectedJob || !filtered.length) return;
    setAiBusy(true); setAiErr('');
    try {
      const matches = await matchCandidatesWithAI(selectedJob, filtered);
      const map: Record<string, AIMatch> = {};
      matches.forEach(m => { map[m.candidate_id] = m; });
      setAiMatches(map);
    } catch (e: any) {
      setAiErr(`AI match unavailable: ${e.message || 'try again shortly'}.`);
    }
    setAiBusy(false);
  };

  const connect = async (c: Candidate) => {
    if (!user || !firm || !selectedJob) return;
    if (connByCandidate(c.id)) return;
    setConnecting(c.id);
    const { data, error } = await supabase.from('talent_connections').insert({
      job_id: selectedJob.id, candidate_id: c.id, manager_firm_id: firm.id,
      requested_by: user.id, status: 'pending',
    }).select().single();
    if (!error && data) {
      setConns(prev => [...prev, data as TalentConnection]);
      logActivity(user.id, firm.id, 'talent_connection_made', 'candidate', c.id, { job_title: selectedJob.title, candidate_name: c.full_name });
    }
    else if (error) alert(error.message);
    setConnecting(null);
  };

  const clearFilters = () => { setFSeniority([]); setFRoles([]); setFAssets([]); setFReloc([]); };
  const filterCount = fSeniority.length + fRoles.length + fAssets.length + fReloc.length;

  if (loading) return <LoadingScreen label="Loading candidates…" />;

  // No active jobs → prompt.
  if (jobs.length === 0) {
    return (
      <div style={{ ...card, padding: '48px', textAlign: 'center', maxWidth: '620px' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>💼</div>
        <div style={{ fontSize: '15px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>Post a job to start searching</div>
        <div style={{ fontSize: '13px', color: C.muted, marginBottom: '18px', lineHeight: 1.6 }}>
          TalentConnect matches candidates against your open roles. Post at least one active job to browse and connect with candidates.
        </div>
        <button onClick={() => goToTab('jobs')} style={btnPrimary}>Post a Job</button>
      </div>
    );
  }

  return (
    <>
      {/* Job selector + AI match */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '320px', flex: 1 }}>
          <label style={labelStyle}>Searching for</label>
          <select value={selectedJobId} onChange={e => setSelectedJobId(e.target.value)} style={{ ...inputStyle, maxWidth: '460px' }}>
            {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowFilters(s => !s)} style={{ ...btnGhost }}>
            Filters{filterCount ? ` (${filterCount})` : ''}
          </button>
          <button onClick={runAIMatch} disabled={aiBusy || !filtered.length} style={{ ...btnPrimary, background: aiMatches ? C.gold : C.navy, color: aiMatches ? C.navy : C.white, opacity: aiBusy || !filtered.length ? 0.6 : 1 }}>
            {aiBusy ? 'Ranking…' : aiMatches ? '✓ AI Ranked' : '✨ AI Match'}
          </button>
          {aiMatches && <button onClick={() => setAiMatches(null)} style={btnGhost}>Clear</button>}
        </div>
      </div>

      {aiErr && <Banner type="err" text={aiErr} />}

      {/* Filters panel */}
      {showFilters && (
        <div style={{ ...card, padding: '20px 22px', marginBottom: '18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
            <div><label style={labelStyle}>Seniority</label><ChipSelect options={SENIORITIES.map(s => ({ value: s.value, label: s.label }))} selected={fSeniority} onToggle={v => setFSeniority(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])} /></div>
            <div><label style={labelStyle}>Asset Classes</label><ChipSelect options={ASSET_CLASSES} selected={fAssets} onToggle={v => setFAssets(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])} /></div>
            <div><label style={labelStyle}>Target Roles</label><ChipSelect options={TARGET_ROLES} selected={fRoles} onToggle={v => setFRoles(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])} /></div>
            <div><label style={labelStyle}>Relocation</label><ChipSelect options={RELOCATION_COUNTRIES} selected={fReloc} onToggle={v => setFReloc(p => p.includes(v) ? p.filter(x => x !== v) : [...p, v])} columns={3} /></div>
          </div>
          {filterCount > 0 && <div style={{ marginTop: '14px' }}><button onClick={clearFilters} style={btnGhost}>Clear filters</button></div>}
        </div>
      )}

      <div style={{ fontSize: '11px', color: C.muted, marginBottom: '14px', fontWeight: 600, letterSpacing: '0.3px' }}>
        {ordered.length} candidate{ordered.length !== 1 ? 's' : ''}{aiMatches ? ' · ranked by AI fit' : ''}
      </div>

      {ordered.length === 0 ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No candidates found</div>
          <div style={{ fontSize: '13px', color: C.muted }}>Try clearing filters, or check back as more candidates join.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
          {ordered.map(c => {
            const conn = connByCandidate(c.id);
            const flagged = flaggedIds.has(c.id);
            const ai = aiMatches?.[c.id];
            return (
              <div key={c.id} style={{ ...card, padding: '18px 20px', display: 'flex', flexDirection: 'column', borderTop: flagged ? `3px solid ${C.gold}` : `1px solid ${C.border}` }}>
                {/* Header row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'Georgia, serif', fontSize: '15.5px', color: C.navy }}>{c.full_name || 'Candidate'}</span>
                      <SeniorityBadge seniority={c.seniority} />
                      {flagged && <span style={{ background: `${C.gold}22`, color: C.amber, border: `1px solid ${C.gold}66`, fontSize: '8.5px', fontWeight: 700, padding: '1px 6px', borderRadius: '2px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>★ Flagged by Altveno</span>}
                    </div>
                    <div style={{ fontSize: '12.5px', color: C.textSec, fontWeight: 500 }}>
                      {c.current_role || 'Role undisclosed'}{!c.hide_employer && c.current_employer ? ` · ${c.current_employer}` : ''}
                    </div>
                  </div>
                  {ai && (
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <div style={{ fontFamily: 'Georgia, serif', fontSize: '20px', color: ai.match_score >= 70 ? C.success : ai.match_score >= 40 ? C.amber : C.muted, lineHeight: 1 }}>{ai.match_score}%</div>
                      <div style={{ fontSize: '8px', color: C.mutedLt, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Match</div>
                    </div>
                  )}
                </div>

                {ai?.reason && <div style={{ fontSize: '11.5px', color: C.amber, fontStyle: 'italic', marginBottom: '10px', lineHeight: 1.5 }}>“{ai.reason}”</div>}

                {(c.target_roles || []).length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '9px', fontWeight: 700, color: C.mutedLt, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Target Roles</div>
                    <Pills items={c.target_roles || []} max={3} />
                  </div>
                )}
                {(c.asset_classes || []).length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '9px', fontWeight: 700, color: C.mutedLt, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Asset Classes</div>
                    <Pills items={c.asset_classes || []} bg={`${C.teal}12`} fg={C.teal} bd={`${C.teal}33`} />
                  </div>
                )}
                {(c.relocation_countries || []).length > 0 && (
                  <div style={{ fontSize: '11.5px', color: C.muted, marginBottom: '12px' }}>
                    <span style={{ fontWeight: 600 }}>Relocation:</span> {(c.relocation_countries || []).join(', ')}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '4px' }}>
                  <button onClick={() => navigate(`/talent-connect/candidate/${c.id}?job=${selectedJobId}`)} style={{ ...btnGhost, flex: 1, padding: '7px 14px', fontSize: '11.5px' }}>View Profile</button>
                  <button onClick={() => connect(c)} disabled={connecting === c.id || !!conn} style={{
                    flex: 1, padding: '7px 14px', fontSize: '11.5px', fontWeight: 600, borderRadius: '3px', fontFamily: 'system-ui',
                    border: conn && conn.status !== 'accepted' ? `1px solid ${C.amber}44` : 'none',
                    background: conn?.status === 'accepted' ? C.success : conn ? C.surface : C.navy,
                    color: conn?.status === 'accepted' ? C.white : conn ? C.amber : C.white,
                    cursor: conn ? 'default' : 'pointer',
                  }}>
                    {connecting === c.id ? '…' : conn?.status === 'accepted' ? 'Connected' : conn?.status === 'declined' ? 'Declined' : conn ? 'Pending' : 'Connect'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ───────────────────────────── My Jobs ───────────────────────────── */

const emptyJob = {
  title: '', description: '', target_roles: [] as string[], asset_classes: [] as string[],
  seniority: [] as string[], location: '', relocation_offered: false, status: 'active',
};

function statusStyle(status: string) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    active: { bg: '#F0FDF4', fg: C.success, label: 'Active' },
    closed: { bg: '#FEF2F2', fg: C.danger, label: 'Closed' },
    draft:  { bg: C.surface, fg: C.muted, label: 'Draft' },
  };
  return map[status] || map.draft;
}

// A candidate "matches" a job if it shares a target role or asset class, or the
// seniority is one the job wants (a light-touch heuristic for the count badge).
function candidateMatchesJob(c: Candidate, job: TalentJob): boolean {
  const roles = job.target_roles || [], assets = job.asset_classes || [], sen = job.seniority || [];
  const roleHit = roles.length > 0 && (c.target_roles || []).some(r => roles.includes(r));
  const assetHit = assets.length > 0 && (c.asset_classes || []).some(a => assets.includes(a));
  const senHit = sen.length > 0 && !!c.seniority && sen.includes(c.seniority);
  if (!roles.length && !assets.length && !sen.length) return true; // untargeted job matches everyone
  return roleHit || assetHit || senHit;
}

function MyJobsTab() {
  const { user } = useAuth();
  const { firm } = useProfile();
  const [, setSearchParams] = useSearchParams();

  const [jobs, setJobs]         = useState<TalentJob[]>([]);
  const [candidates, setCands]  = useState<Candidate[]>([]);
  const [connections, setConns] = useState<TalentConnection[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState<TalentJob | null>(null);
  const [form, setForm]         = useState({ ...emptyJob });
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  useEffect(() => { if (firm !== undefined) load(); /* eslint-disable-next-line */ }, [firm]);

  async function load() {
    setLoading(true);
    if (!firm) { setLoading(false); return; }
    const [{ data: jobData }, { data: candData }, { data: connData }] = await Promise.all([
      supabase.from('talent_jobs').select('*').eq('firm_id', firm.id).order('created_at', { ascending: false }),
      supabase.from('candidates').select('*').eq('visible_on_platform', true),
      supabase.from('talent_connections').select('*').eq('manager_firm_id', firm.id),
    ]);
    setJobs((jobData as TalentJob[]) || []);
    setCands((candData as Candidate[]) || []);
    setConns((connData as TalentConnection[]) || []);
    setLoading(false);
  }

  const startAdd = () => { setEditing(null); setForm({ ...emptyJob }); setErr(''); setShowForm(true); };
  const startEdit = (j: TalentJob) => {
    setEditing(j);
    setForm({
      title: j.title, description: j.description || '', target_roles: j.target_roles || [],
      asset_classes: j.asset_classes || [], seniority: j.seniority || [], location: j.location || '',
      relocation_offered: j.relocation_offered, status: j.status,
    });
    setErr(''); setShowForm(true);
  };

  const toggle = (k: 'target_roles' | 'asset_classes' | 'seniority', v: string) =>
    setForm(f => ({ ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v] }));

  const save = async () => {
    if (!form.title.trim()) { setErr('Job title is required.'); return; }
    if (!firm || !user) { setErr('You need a firm profile to post a job.'); return; }
    setSaving(true); setErr('');
    const payload = {
      title: form.title.trim(), description: form.description.trim() || null,
      target_roles: form.target_roles, asset_classes: form.asset_classes, seniority: form.seniority,
      location: form.location.trim() || null, relocation_offered: form.relocation_offered, status: form.status,
    };
    let error;
    let newJobId: string | undefined;
    if (editing) {
      ({ error } = await supabase.from('talent_jobs').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', editing.id));
    } else {
      const res = await supabase.from('talent_jobs').insert({ ...payload, firm_id: firm.id, posted_by: user.id }).select('id').single();
      error = res.error; newJobId = res.data?.id;
    }
    setSaving(false);
    if (error) { setErr(error.message); return; }
    if (!editing) logActivity(user.id, firm.id, 'job_posted', 'job', newJobId || payload.title, { job_title: payload.title });
    setShowForm(false); setEditing(null); setForm({ ...emptyJob });
    load();
  };

  const setStatus = async (j: TalentJob, status: string) => {
    await supabase.from('talent_jobs').update({ status, updated_at: new Date().toISOString() }).eq('id', j.id);
    load();
  };

  const counts = useMemo(() => {
    const m: Record<string, { matched: number; sent: number }> = {};
    jobs.forEach(j => {
      m[j.id] = {
        matched: candidates.filter(c => candidateMatchesJob(c, j)).length,
        sent: connections.filter(c => c.job_id === j.id).length,
      };
    });
    return m;
  }, [jobs, candidates, connections]);

  // Switch to the candidates tab (default) pre-scoped to a job.
  const findCandidates = (j: TalentJob) => {
    setSearchParams({ job: j.id });
  };

  if (loading) return <LoadingScreen label="Loading your jobs…" />;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '18px' }}>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Post roles and search TalentConnect for candidates</p>
        {firm && !showForm && <button onClick={startAdd} style={btnPrimary}>+ Post New Job</button>}
      </div>

      {!firm && <Banner type="info" text="You need a firm profile before you can post jobs." />}

      {showForm && (
        <div style={{ ...card, padding: '24px 26px', marginBottom: '20px', maxWidth: '760px' }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy, marginBottom: '16px' }}>{editing ? 'Edit Job' : 'New Job'}</div>
          {err && <Banner type="err" text={err} />}
          <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Job Title *</label><input style={inputStyle} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Senior Portfolio Manager, Systematic Equity" /></div>
          <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Description</label><textarea rows={4} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Responsibilities, requirements and what makes this role compelling…" /></div>
          <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Target Roles</label><ChipSelect options={TARGET_ROLES} selected={form.target_roles} onToggle={v => toggle('target_roles', v)} /></div>
          <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Asset Classes</label><ChipSelect options={ASSET_CLASSES} selected={form.asset_classes} onToggle={v => toggle('asset_classes', v)} /></div>
          <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Seniority Wanted</label><ChipSelect options={SENIORITIES.map(s => ({ value: s.value, label: s.label }))} selected={form.seniority} onToggle={v => toggle('seniority', v)} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div><label style={labelStyle}>Location</label><input style={inputStyle} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. New York, NY" /></div>
            <div><label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: C.textSec, cursor: 'pointer', marginBottom: '20px' }}>
            <input type="checkbox" checked={form.relocation_offered} onChange={e => setForm(f => ({ ...f, relocation_offered: e.target.checked }))} /> Relocation offered
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={save} disabled={saving || !form.title.trim()} style={{ ...btnPrimary, opacity: saving || !form.title.trim() ? 0.6 : 1 }}>{saving ? 'Saving…' : editing ? 'Update Job' : 'Post Job'}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} style={btnGhost}>Cancel</button>
          </div>
        </div>
      )}

      {firm && jobs.length === 0 && !showForm ? (
        <div style={{ ...card, padding: '48px', textAlign: 'center', maxWidth: '760px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>💼</div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No jobs posted yet</div>
          <div style={{ fontSize: '13px', color: C.muted, marginBottom: '16px' }}>Post your first role to start searching for candidates on TalentConnect.</div>
          <button onClick={startAdd} style={btnPrimary}>+ Post New Job</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '860px' }}>
          {jobs.map(j => {
            const ss = statusStyle(j.status);
            const cnt = counts[j.id] || { matched: 0, sent: 0 };
            return (
              <div key={j.id} style={{ ...card, padding: '18px 22px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '3px' }}>
                      <span style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.navy }}>{j.title}</span>
                      <span style={{ background: ss.bg, color: ss.fg, border: `1px solid ${ss.fg}33`, fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>{ss.label}</span>
                    </div>
                    <div style={{ fontSize: '11.5px', color: C.muted }}>{j.location ? `${j.location} · ` : ''}Posted {fmtDate(j.created_at)}</div>
                  </div>
                </div>

                {(j.target_roles || []).length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {(j.target_roles || []).slice(0, 6).map(r => (
                      <span key={r} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSec, fontSize: '10px', fontWeight: 500, padding: '2px 7px', borderRadius: '2px' }}>{r}</span>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', paddingTop: '12px', borderTop: `1px solid ${C.borderLt}`, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '18px', fontSize: '12px', color: C.muted }}>
                    <span><strong style={{ color: C.navy }}>{cnt.matched}</strong> candidate{cnt.matched !== 1 ? 's' : ''} matched</span>
                    <span><strong style={{ color: C.navy }}>{cnt.sent}</strong> connection{cnt.sent !== 1 ? 's' : ''} sent</span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => findCandidates(j)} style={{ ...btnPrimary, padding: '6px 14px', fontSize: '11.5px' }}>Find Candidates</button>
                    <button onClick={() => startEdit(j)} style={{ ...btnGhost, padding: '6px 12px' }}>Edit</button>
                    {j.status === 'active'
                      ? <button onClick={() => setStatus(j, 'closed')} style={{ ...btnGhost, padding: '6px 12px', color: C.danger, borderColor: `${C.danger}44` }}>Close</button>
                      : <button onClick={() => setStatus(j, 'active')} style={{ ...btnGhost, padding: '6px 12px', color: C.success, borderColor: `${C.success}44` }}>Reopen</button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
